import type { NextLink, Operation, FetchResult, ApolloError } from '@apollo/client/core';
import { ApolloLink, Observable } from '@apollo/client/core';
import { getMainDefinition } from '@apollo/client/utilities';

import { extractErrorInfo, formatErrorMessage } from '../error-handling/error-handler';

import type { ExtendedApolloContext, NotificationContextOptions } from './types';

interface NotificationLinkOptions {
  /**
   * Function to add notifications to the UI
   * Should match the signature from useNotifications().add()
   */
  addNotification: (notification: { type: 'error' | 'success'; title: string; duration?: number }) => void;
}

/**
 * Helper function to process results for notifications
 */
function processResultForNotifications(
  result: FetchResult,
  operation: Operation,
  notificationOptions: NotificationContextOptions,
  addNotification: NotificationLinkOptions['addNotification'],
) {
  // Handle success notifications for mutations
  if (!result.errors && notificationOptions.successMessage) {
    const definition = getMainDefinition(operation.query);
    const isMutation = definition.kind === 'OperationDefinition' && definition.operation === 'mutation';

    if (isMutation) {
      addNotification({
        type: 'success',
        title: notificationOptions.successMessage,
      });
    }
  }

  // Handle error notifications
  if (result.errors && result.errors.length > 0) {
    if (!notificationOptions.preventErrorNotification) {
      // Create a mock ApolloError-like object for error extraction
      const mockError = {
        graphQLErrors: result.errors,
        networkError: null,
        message: result.errors[0]?.message || 'GraphQL error occurred',
      };

      const errorInfo = extractErrorInfo(mockError as ApolloError);

      // Prioritize backend error messages over fallbacks
      // Only use fallbackErrorMessage when backend provides no meaningful message
      const hasBackendMessage =
        errorInfo.message &&
        !errorInfo.message.includes('An unexpected error occurred') &&
        !errorInfo.message.includes('Network error occurred');

      const displayMessage = hasBackendMessage
        ? errorInfo.message
        : notificationOptions.fallbackErrorMessage || formatErrorMessage(errorInfo);

      addNotification({
        type: 'error',
        title: displayMessage,
      });
    }
  }
}

/**
 * Apollo Link that automatically handles error and success notifications
 * based on operation context configuration
 */
export function createNotificationLink(options: NotificationLinkOptions): ApolloLink {
  const { addNotification } = options;

  return new ApolloLink((operation: Operation, forward: NextLink) => {
    const context = operation.getContext() as ExtendedApolloContext;
    const notificationOptions = context.notifications || {};

    return new Observable((observer) => {
      const subscription = forward(operation).subscribe({
        next: (result: FetchResult) => {
          // Process the result for notifications
          processResultForNotifications(result, operation, notificationOptions, addNotification);

          observer.next(result);
        },
        error: (networkError: Error & { result?: { data?: FetchResult['data']; errors?: FetchResult['errors'] } }) => {
          // Check if this network error contains GraphQL errors in the result
          if (networkError.result?.errors) {
            // Create a fake result to process the errors
            const fakeResult: FetchResult = {
              data: networkError.result.data,
              errors: networkError.result.errors,
            };

            processResultForNotifications(fakeResult, operation, notificationOptions, addNotification);
          }

          // Still propagate the error
          observer.error(networkError);
        },
        complete: () => {
          observer.complete();
        },
      });

      return () => subscription.unsubscribe();
    });
  });
}
